import React, { useEffect, useState, useCallback, useRef } from 'react'; // useRef eklendi
import { useParams, Link } from 'react-router-dom';
import { useApi } from '../services/api';
import { Helmet } from 'react-helmet-async';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import styles from './BusStopDetailPage.module.css';
import LineChip from '../components/common/LineChip';

// React Icons ile ikonları import ediyoruz
import { 
  IoArrowBack, 
  IoBus, 
  IoAccessibilityOutline, 
  IoBicycle,
  IoCopy,
  IoGlobe
} from 'react-icons/io5';

// Default marker icon sorunu çözümü
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Otobüs durağı veri tipi tanımlama
interface BusStopDetail {
  bus_stop_id: number;
  bus_stop_name: string;
  latitude: number;
  longitude: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  serving_bus_lines: string[];
  photo_url?: string;
  photos?: BusStopPhoto[];
  bus_line_details: {
    bus_line_id: string;
    bus_line_name: string;
    bus_line_path_description: string;
    description: string;
    start_bus_stop: string;
    end_bus_stop: string;
  }[];
  bus_stop_live_vehicles: {
    bus_stops_remaining: number;
    bus_line_direction: number;
    latitude: number;
    longitude: number;
    bicycle_rack_available: boolean;
    wheelchair_accessible: boolean;
    bus_line_id: string;
    bus_line_name: string;
    vehicle_id: number;
  }[];
}

// Durak fotoğrafı için arayüz ekleyelim
interface BusStopPhoto {
  photo_id: number;
  url: string;
  width?: number;
  height?: number;
  description?: string;
  is_primary: boolean;
  created_at: string;
}

const BusStopDetailPage: React.FC = () => {
  const { stopId } = useParams<{ stopId: string }>();
  const { getBusStopDetail } = useApi();
  const [busStopData, setBusStopData] = useState<BusStopDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [isFirstLoad, setIsFirstLoad] = useState<boolean>(true);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  // getBusStopDetail fonksiyonunun en son halini tutmak için ref
  const getBusStopDetailRef = useRef(getBusStopDetail);

  // getBusStopDetail değişirse ref'i güncelle
  useEffect(() => {
    getBusStopDetailRef.current = getBusStopDetail;
  }, [getBusStopDetail]);
  
  // Resim URL kontrolü için yardımcı fonksiyon
  const isValidImageUrl = (url?: string) => {
    return url && !url.includes('api.vagonla.com'); // Geçersiz domain kontrolü
  };

  // fetchBusStopDetail fonksiyonunu useCallback ile sarmak
  const fetchBusStopDetail = useCallback(async () => {
    try {
      // İlk yükleme kontrolü
      if (isFirstLoad) {
        setLoading(true);
        setError(null);
      } else {
        // Sessiz yenileme (silent refresh)
        setError(null);
      }

      if (!stopId) {
        setError('Durak kimliği belirtilmedi');
        setLoading(false);
        return;
      }

      const response = await getBusStopDetailRef.current(stopId); // Ref üzerinden çağrı yapıldı
      
      if (response && response.status === 'success') {
        // Ek doğrulama - resim URL'lerini kontrol et ve geçersizleri temizle
        if (response.data) {
          // Ana fotoğrafı kontrol et
          if (!isValidImageUrl(response.data.photo_url)) {
            response.data.photo_url = undefined;
          }
          
          // Fotoğraf dizisini kontrol et
          if (response.data.photos && response.data.photos.length > 0) {
            response.data.photos = response.data.photos.filter((photo: BusStopPhoto) => 
              isValidImageUrl(photo.url)
            );
          }
        }
        
        setBusStopData(response.data);
        setLastRefresh(new Date());
      } else {
        setError('Durak bilgisi alınamadı');
      }
    } catch (err: any) {
      console.error('Durak bilgisi yüklenirken hata:', err);
      setError(err.message || 'Durak bilgisi yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
      setIsFirstLoad(false);
    }
  }, [stopId, isFirstLoad]); // getBusStopDetail bağımlılıktan çıkarıldı, çünkü ref kullanılıyor.
                               // setLoading, setError, setIsFirstLoad gibi state setter'ları kararlıdır.

  useEffect(() => {
    // İlk yüklemeyi yap
    fetchBusStopDetail();
    
    // 20 saniyede bir yenileme yapacak interval
    const refreshInterval = setInterval(() => {
      console.log('Durak bilgileri yenileniyor...');
      fetchBusStopDetail();
    }, 20000); // 20 saniye
    
    // Component unmount edildiğinde interval'i temizle
    return () => {
      clearInterval(refreshInterval);
    };
  }, [fetchBusStopDetail]); // fetchBusStopDetail'in bağımlılıkları değiştiği için bu useEffect doğru şekilde çalışacaktır.

  const handleCopyLocation = () => {
    setCopyMessage('Konum kopyalandı');
    setTimeout(() => setCopyMessage(null), 3000);
  };

  const openInMaps = () => {
    if (busStopData) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${busStopData.latitude},${busStopData.longitude}`, '_blank');
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>Durak bilgileri yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.busStopDetailPage}>
        <div className={styles.errorAlert}>{error}</div>
        <Link to="/stops" className={styles.backButton}>
          <IoArrowBack className={styles.backButtonIcon} />
          Duraklar Sayfasına Dön
        </Link>
      </div>
    );
  }

  if (!busStopData) {
    return (
      <div className={styles.busStopDetailPage}>
        <div className={styles.infoAlert}>Durak bilgisi bulunamadı</div>
        <Link to="/stops" className={styles.backButton}>
          <IoArrowBack className={styles.backButtonIcon} />
          Duraklar Sayfasına Dön
        </Link>
      </div>
    );
  }

  return (
    <>
      {busStopData && (
        <Helmet>
          <title>{`${busStopData.bus_stop_id ?? 'Durak ID'} - ${busStopData.bus_stop_name ?? 'Durak Adı'} Durağı - vagonla`}</title>
          <meta name="description" content={`${busStopData.bus_stop_name ?? 'Bu durak'} durağından geçen otobüs hatları ve durak detayları`} />
        </Helmet>
      )}

      <div className={styles.busStopDetailPage}>
        <div className={styles.backButtonContainer}>
          <Link to="/stops" className={styles.backButton}>
            <IoArrowBack className={styles.backButtonIcon} />
            Duraklar
          </Link>
        </div>

        {/* Son güncelleme bilgisi */}
        {!loading && busStopData && (
          <p className={styles.lastUpdated}>
            Son güncelleme: {format(lastRefresh, 'HH:mm:ss', { locale: tr })}
          </p>
        )}

        {copyMessage && (
          <div className={styles.infoAlert}>{copyMessage}</div>
        )}

        <div className={styles.grid}>
          {/* Durak başlık bilgileri - Paylaş butonu kaldırıldı */}
          <div className={`${styles.card} ${styles.fullWidth}`}>
            <div className={styles.header}>
              <div>
                <h1 className={styles.busStopTitle}>{busStopData.bus_stop_name}</h1>
                <p className={styles.busStopId}>Durak No: {busStopData.bus_stop_id}</p>
              </div>
              <div className={styles.actionButtons}>
                <CopyToClipboard 
                  text={`${busStopData.latitude},${busStopData.longitude}`}
                  onCopy={handleCopyLocation}
                >
                  <button className={styles.actionButton}>
                    <IoCopy />
                    Kopyala
                  </button>
                </CopyToClipboard>
                <button className={styles.actionButton} onClick={openInMaps}>
                  <IoGlobe />
                  Haritada Aç
                </button>
              </div>
            </div>
          </div>

          {/* Yaklaşan otobüsler */}
          <div className={styles.card}>
            <h2 className={styles.sectionTitle}>Yaklaşan Otobüsler</h2>
            {/* Bu bölüm API'den alınan canlı verileri kullanır, mock veri kullanılmamaktadır. */}
            {busStopData.bus_stop_live_vehicles && busStopData.bus_stop_live_vehicles.length > 0 ? (
              <div className={styles.upcomingBusList}>
                {busStopData.bus_stop_live_vehicles.map((vehicle) => (
                  <div key={vehicle.vehicle_id} className={styles.busItem}>
                    <div className={styles.busInfo}>
                      <IoBus className={styles.busIcon} />
                      <div className={styles.busDetails}>
                        <Link to={`/lines/bus/${vehicle.bus_line_id}`} className={styles.busLineTitle}>
                          {vehicle.bus_line_id} - {vehicle.bus_line_name}
                        </Link>
                        <span className={styles.busStopsRemaining}>
                          {vehicle.bus_stops_remaining} durak sonra gelecek
                        </span>
                      </div>
                    </div>
                    <div className={styles.busFeatures}>
                      {vehicle.wheelchair_accessible && (
                        <span className={styles.featureChip}>
                          <IoAccessibilityOutline />
                          Engelli Erişimi
                        </span>
                      )}
                      {vehicle.bicycle_rack_available && (
                        <span className={styles.featureChip}>
                          <IoBicycle />
                          Bisiklet Taşıma
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.infoAlert}>
                Şu anda bu durağa yaklaşan otobüs bulunmuyor.
              </div>
            )}
          </div>

          {/* Harita */}
          <div className={styles.card}>
            <h2 className={styles.sectionTitle}>Konum</h2>
            <div className={styles.mapContainer}>
              <MapContainer 
                center={[busStopData.latitude, busStopData.longitude]} 
                zoom={15} 
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={false}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Marker position={[busStopData.latitude, busStopData.longitude]}>
                  <Popup>
                    <strong>{busStopData.bus_stop_name}</strong><br />
                    Durak No: {busStopData.bus_stop_id}
                  </Popup>
                </Marker>
                {/* Yaklaşan otobüsler */}
                {busStopData.bus_stop_live_vehicles && busStopData.bus_stop_live_vehicles.map((vehicle) => (
                  <Marker 
                    key={vehicle.vehicle_id}
                    position={[vehicle.latitude, vehicle.longitude]}
                    icon={L.divIcon({
                      html: `<div style="background-color: #1976d2; color: white; border-radius: 50%; width: 24px; height: 24px; display: flex; justify-content: center; align-items: center; font-weight: bold;">${vehicle.bus_line_id}</div>`,
                      className: '',
                      iconSize: [24, 24],
                      iconAnchor: [12, 12]
                    })}
                  >
                    <Popup>
                      <strong>Hat {vehicle.bus_line_id}</strong><br />
                      {vehicle.bus_line_name}<br />
                      {vehicle.bus_stops_remaining} durak sonra gelecek<br />
                      {vehicle.wheelchair_accessible ? 'Engelli erişimine uygun' : 'Engelli erişimine uygun değil'}<br />
                      {vehicle.bicycle_rack_available ? 'Bisiklet taşıma aparatı var' : 'Bisiklet taşıma aparatı yok'}
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          </div>

          {/* Geçen hatlar - Düzenlenmiş liste şeklinde */}
          <div className={`${styles.card} ${styles.fullWidth}`}>
            <h2 className={styles.sectionTitle}>Bu Duraktan Geçen Otobüs Hatları</h2>
            <div className={styles.linesList}>
              {busStopData.bus_line_details.map((line) => (
                <Link to={`/lines/bus/${line.bus_line_id}`} key={line.bus_line_id} className={styles.lineListItem}>
                  <div className={styles.lineItemContent}>
                    <div className={styles.lineItemHeader}>
                      <LineChip 
                        lineNumber={line.bus_line_id} 
                        size="normal"
                      />
                      <h3 className={styles.lineTitle}>
                        {line.bus_line_name}
                      </h3>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Fotoğraflar - eğer varsa ve geçerli URL'ler içeriyorsa */}
          {busStopData.photos && busStopData.photos.length > 0 && (
            <div className={`${styles.card} ${styles.fullWidth}`}>
              <h2 className={styles.sectionTitle}>Fotoğraflar</h2>
              <div className={styles.photosGrid}>
                {busStopData.photos.map((photo: BusStopPhoto) => (
                  <div key={photo.photo_id} className={styles.photoCard}>
                    {isValidImageUrl(photo.url) ? (
                      <img 
                        src={photo.url} 
                        alt={photo.description || `${busStopData.bus_stop_name} durağı`}
                        className={styles.busStopPhoto}
                        onError={(e) => {
                          // Resim yüklenemezse, hatayı gizle
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className={styles.photoPlaceholder}>
                        Fotoğraf yüklenemedi
                      </div>
                    )}
                    {photo.description && (
                      <div className={styles.photoCaption}>
                        {photo.description}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BusStopDetailPage;
